import type { ReactNode } from 'react';
import { useState } from 'react';

import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@crossnokaye/ux-common';

import { PreviewChange } from '@/components/settings/preview-change';

import type { PreviewChangeData } from './setting-preview.types';

export interface DirtyChangeTooltipProps {
  children: ReactNode;
  /** When set, hover shows PreviewChange (new <- old). Takes priority. */
  changePreview?: PreviewChangeData | null;
  /** Fallback tooltip when there is no change (e.g. bulk-edit disabled cell). */
  tooltipContent?: ReactNode;
}

/**
 * Conditionally wraps children in a hover tooltip showing a dirty-field change
 * preview (new <- old) or a plain fallback message. Pure presentational — consumers
 * supply pre-formatted strings and own their outer cell layout.
 *
 * Tooltip content is lazy-mounted on first hover to avoid per-cell Radix overhead
 * when many dirty cells are visible.
 */
export const DirtyChangeTooltip = ({
  children,
  changePreview,
  tooltipContent,
}: DirtyChangeTooltipProps): ReactNode => {
  const [open, setOpen] = useState(false);

  const tooltipBody = changePreview ? (
    <PreviewChange
      displayCurrent={changePreview.displayCurrent}
      displayOriginal={changePreview.displayOriginal}
    />
  ) : (
    tooltipContent
  );

  if (!tooltipBody) {
    return children;
  }

  return (
    <Tooltip delayDuration={0} open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild onPointerEnter={() => setOpen(true)}>
        <div className="w-full" data-component="DirtyChangeTooltip">
          {children}
        </div>
      </TooltipTrigger>
      {open ? (
        <TooltipPortal>
          <TooltipContent>{tooltipBody}</TooltipContent>
        </TooltipPortal>
      ) : null}
    </Tooltip>
  );
};
